import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "../baseQuery";
import {
  TaskExecution,
  PaginatedTaskExecutionResponse,
  getTaskHubExecutionParams,
  GetMyTaskExecutionsResponse,
  CountMyTaskExecutionsResponse,
} from "./models";

export const taskHubExecutionApi = createApi({
  baseQuery,
  reducerPath: "taskHubApiExecution",
  tagTypes: ["Tasks-Execution"],
  endpoints: (builder) => ({
    getTasksExecution: builder.query<
      PaginatedTaskExecutionResponse,
      getTaskHubExecutionParams
    >({
      query: (params) => ({
        url: "/api/v1/taskexecutions/",
        method: "GET",
        params,
      }),
      transformResponse: (
        response: TaskExecution[] | PaginatedTaskExecutionResponse
      ): PaginatedTaskExecutionResponse => {
        if (Array.isArray(response)) {
          return {
            count: response.length,
            next: null,
            previous: null,
            results: response,
          };
        }

        return response;
      },
      providesTags: (_result, _error) => [
        { type: "Tasks-Execution", id: "LIST" },
      ],
    }),
    updateTaskExecution: builder.mutation<
      TaskExecution,
      { id: number; data: Partial<TaskExecution> }
    >({
      query: ({ id, data }) => ({
        url: `/api/v1/taskexecutions/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Tasks-Execution", id: "LIST" },
        { type: "Tasks-Execution", id: "MY_COUNT" },
        { type: "Tasks-Execution", id: "MY_SUMMARY" },
        { type: "Tasks-Execution", id },
      ],
    }),
    createTaskExecution: builder.mutation<
      TaskExecution,
      Partial<TaskExecution>
    >({
      query: (data) => ({
        url: `/api/v1/taskexecutions/`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: [
        { type: "Tasks-Execution", id: "LIST" },
        { type: "Tasks-Execution", id: "MY_COUNT" },
        { type: "Tasks-Execution", id: "MY_SUMMARY" },
      ],
    }),
    getMyTaskExecutionsCount: builder.query<
      CountMyTaskExecutionsResponse,
      void
    >({
      query: () => ({
        url: "/api/v1/taskexecutions/count_my_task_executions/",
        method: "GET",
      }),
      providesTags: [{ type: "Tasks-Execution", id: "MY_COUNT" }],
    }),
    getMyTaskExecutions: builder.query<GetMyTaskExecutionsResponse, void>({
      query: () => ({
        url: "/api/v1/taskexecutions/get_my_task_executions/",
        method: "GET",
      }),
      providesTags: [{ type: "Tasks-Execution", id: "MY_SUMMARY" }],
    }),
    getTaskExecutionById: builder.query<
      TaskExecution,
      { id: number; expand?: string }
    >({
      query: ({ id, expand }) => ({
        url: `/api/v1/taskexecutions/${id}/`,
        method: "GET",
        params: expand ? { expand } : undefined,
      }),
      providesTags: (_result, _error, { id }) => [
        { type: "Tasks-Execution", id },
      ],
    }),
    uploadTaskExecutionFile: builder.mutation<void, { id: number; file: File }>(
      {
        query: ({ id, file }) => {
          const formData = new FormData();
          formData.append("file", file);

          return {
            url: `/api/v1/taskexecutions/${id}/file/`,
            method: "POST",
            body: formData,
          };
        },
        invalidatesTags: (_result, _error, { id }) => [
          { type: "Tasks-Execution", id },
        ],
      }
    ),
    deleteTaskExecutionFile: builder.mutation<
      void,
      { id: number; fileId: number }
    >({
      query: ({ id, fileId }) => ({
        url: `/api/v1/taskexecutions/${id}/file/${fileId}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "Tasks-Execution", id },
      ],
    }),
  }),
});

export const {
  useGetTasksExecutionQuery,
  useUpdateTaskExecutionMutation,
  useCreateTaskExecutionMutation,
  useGetMyTaskExecutionsCountQuery,
  useGetMyTaskExecutionsQuery,
  useGetTaskExecutionByIdQuery,
  useUploadTaskExecutionFileMutation,
  useDeleteTaskExecutionFileMutation,
} = taskHubExecutionApi;
